// src/components/VideoPlayer.tsx
import { useRef, useEffect, forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { ZoomRegion, DEFAULT_ZOOM_REGIONS } from '../types/capsule';

export type ZoomPreset = 'full' | 'rightHand' | 'leftHand';

const VOLUME_STORAGE_KEY = 'fingerstyle-video-volume';

function loadSavedVolume(): number {
  try {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (saved !== null) {
      const val = parseFloat(saved);
      if (!isNaN(val) && val >= 0 && val <= 1) return val;
    }
  } catch {
    // localStorage unavailable
  }
  return 1;
}

interface VideoPlayerProps {
  src: string;
  zoomRegions?: {
    rightHand?: ZoomRegion;
    leftHand?: ZoomRegion;
  };
  onVideoReady?: (video: HTMLVideoElement) => void;
  onVolumeChange?: (volume: number) => void;
  style?: React.CSSProperties;
  /** Show native browser video controls (used for standalone playback like intro) */
  showControls?: boolean;
  /** Hide the vertical pan (up/down) controls */
  hidePanControls?: boolean;
}

export interface VideoPlayerRef {
  getVideoElement: () => HTMLVideoElement | null;
  setZoom: (preset: ZoomPreset) => void;
  getCurrentZoom: () => ZoomPreset;
  getVolume: () => number;
  setVolume: (vol: number) => void;
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  ({ src, zoomRegions, onVideoReady, onVolumeChange, style, showControls, hidePanControls }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [currentZoom, setCurrentZoom] = useState<ZoomPreset>('full');
    const [volume, setVolumeState] = useState<number>(loadSavedVolume);

    // Vertical pan offset in pixels (0 = no shift)
    const [panY, setPanY] = useState(0);
    const PAN_STEP = 15;

    const setVolume = useCallback((vol: number) => {
      const clamped = Math.max(0, Math.min(1, vol));
      setVolumeState(clamped);
      if (videoRef.current) {
        videoRef.current.volume = clamped;
      }
      try {
        localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
      } catch {
        // localStorage unavailable
      }
      onVolumeChange?.(clamped);
    }, [onVolumeChange]);

    // Apply saved volume when video element is ready
    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = volume;
      }
    }, [src]); // re-apply when src changes

    // Get effective zoom regions (use provided or defaults)
    const effectiveRegions = {
      rightHand: zoomRegions?.rightHand || DEFAULT_ZOOM_REGIONS.rightHand,
      leftHand: zoomRegions?.leftHand || DEFAULT_ZOOM_REGIONS.leftHand,
    };

    // Apply zoom transform
    const getZoomStyles = (): React.CSSProperties => {
      if (currentZoom === 'full') {
        return {
          transform: 'none',
          transformOrigin: 'center',
        };
      }

      const region = currentZoom === 'rightHand'
        ? effectiveRegions.rightHand
        : effectiveRegions.leftHand;

      // Calculate scale to fit entire rectangle in view
      const scaleX = 100 / region.width;
      const scaleY = 100 / region.height;
      const scale = Math.min(scaleX, scaleY);

      // Calculate the center of the rectangle
      const centerX = region.x + region.width / 2;
      const centerY = region.y + region.height / 2;

      // After scaling around (50%, 50%), a point at (centerX, centerY) moves to:
      // newX = 50 + scale * (centerX - 50)
      // newY = 50 + scale * (centerY - 50)
      // We need to translate to bring it back to center (50%, 50%)
      const translateX = (50 - centerX) * scale;
      const translateY = (50 - centerY) * scale;

      return {
        transform: `translate(${translateX}%, ${translateY}%) scale(${scale})`,
        transformOrigin: '50% 50%',
        transition: 'transform 0.3s ease',
      };
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getVideoElement: () => videoRef.current,
      setZoom: (preset: ZoomPreset) => setCurrentZoom(preset),
      getCurrentZoom: () => currentZoom,
      getVolume: () => volume,
      setVolume,
    }), [currentZoom, volume, setVolume]);

    // Notify when video is ready
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleCanPlay = () => {
        onVideoReady?.(video);
      };

      video.addEventListener('canplay', handleCanPlay);

      // If already ready
      if (video.readyState >= 3) {
        onVideoReady?.(video);
      }

      return () => {
        video.removeEventListener('canplay', handleCanPlay);
      };
    }, [onVideoReady]);

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: '#000',
          position: 'relative',
          ...style,
        }}
      >
        {/* Pan wrapper — translateY shifts the video without changing zoom or fit */}
        <div style={{
          width: '100%',
          height: '100%',
          transform: panY !== 0 ? `translateY(${panY}px)` : undefined,
          transition: 'transform 0.15s ease',
        }}>
          <video
            ref={videoRef}
            src={src}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              ...getZoomStyles(),
            }}
            controls={showControls}
            playsInline
            preload="auto"
          />
        </div>
        {/* Vertical pan controls */}
        {!hidePanControls && <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          opacity: 0.6,
          transition: 'opacity 0.2s',
          zIndex: 5,
        }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
        >
          <button
            onClick={() => setPanY((prev) => prev - PAN_STEP)}
            title="Pan video up"
            style={{
              width: 28, height: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              border: 'none', borderRadius: '4px 4px 0 0',
              cursor: 'pointer', fontSize: 14, lineHeight: 1,
            }}
          >&#9650;</button>
          {panY !== 0 && (
            <button
              onClick={() => setPanY(0)}
              title="Reset to center"
              style={{
                width: 28, height: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                border: 'none', cursor: 'pointer', fontSize: 9, lineHeight: 1,
              }}
            >reset</button>
          )}
          <button
            onClick={() => setPanY((prev) => prev + PAN_STEP)}
            title="Pan video down"
            style={{
              width: 28, height: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              border: 'none', borderRadius: '0 0 4px 4px',
              cursor: 'pointer', fontSize: 14, lineHeight: 1,
            }}
          >&#9660;</button>
        </div>}
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';
